// The only place patients, providers and office admins diverge — and only in MESSAGING.
//
// LIMITATION: POST /patient-invite/claim returns just { email }; it does not say which
// kind of account was claimed. So the kind comes from `?kind=` on the link (the API sets
// `provider` or `admin` on every staff link), and anything else means a patient.
// Nothing security-relevant hangs off it — the wrong value only shows the wrong
// "what next" copy.
import type { ReactNode } from 'react';
import { card } from '../components/ui';
import { ClaimShell, IconBadge } from './ClaimShell';

const APP_STORE = import.meta.env.VITE_APP_STORE_URL || '';
const PLAY_STORE = import.meta.env.VITE_PLAY_STORE_URL || '';
const ADMIN_WEB = (import.meta.env.VITE_ADMIN_WEB_URL || 'https://admin.expyhealth.com').replace(/\/$/, '');

// Who the link is for. Absent or unknown means a patient, by far the common case.
export type ClaimKind = 'patient' | 'provider' | 'admin';

export function claimKindFrom(raw: string | null): ClaimKind {
  const value = (raw || '').toLowerCase();
  return value === 'provider' || value === 'admin' ? value : 'patient';
}

// Providers and office admins sign in on the admin web; patients use the app.
export const isStaff = (kind: ClaimKind) => kind !== 'patient';

// The small label at the top of every claim screen. Patients get none.
export function claimBadge(kind: ClaimKind): string | undefined {
  if (kind === 'provider') return 'For providers';
  if (kind === 'admin') return 'Team account';
  return undefined;
}

export function ClaimSuccess({ email, kind }: { email?: string; kind: ClaimKind }) {
  return (
    <ClaimShell badge={claimBadge(kind)}>
      <IconBadge>
        <svg width={30} height={30} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M4.5 12.5l4.5 4.5L19.5 6.5"
            stroke="var(--teal-deep)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconBadge>
      <h1 tabIndex={-1} className="pretty" style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.16, margin: 0 }}>
        Your account is ready.
      </h1>
      <p className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', margin: '12px 0 0', lineHeight: 1.55 }}>
        {kind === 'provider' && 'Sign in to your provider account to see your patients.'}
        {kind === 'admin' && 'You can now use the Expy Health portal.'}
        {kind === 'patient' && 'Your plan is waiting on your phone.'}
        {email && (
          <>
            {' '}
            Sign in with <strong style={{ color: 'var(--ink)' }}>{email}</strong> and the password you just set.
          </>
        )}
      </p>

      {isStaff(kind) ? (
        <div style={{ marginTop: 26 }}>
          <a
            href={`${ADMIN_WEB}/login`}
            style={{
              display: 'inline-block',
              background: 'var(--teal)',
              color: '#fff',
              fontSize: 16,
              fontWeight: 800,
              padding: '15px 30px',
              borderRadius: 10,
              boxShadow: 'var(--teal-shadow)',
            }}
          >
            Go to sign in
          </a>
        </div>
      ) : (
        <div style={{ marginTop: 26 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Get the Expy app</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 4, lineHeight: 1.5 }}>
            Download it, sign in with the password you just set, and your first session is there.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <StoreBadge href={APP_STORE} label="Download on iOS" />
            <StoreBadge href={PLAY_STORE} label="Get it on Android" />
          </div>
        </div>
      )}
    </ClaimShell>
  );
}

// The store URLs are build-time env (VITE_APP_STORE_URL / VITE_PLAY_STORE_URL); if one is
// unset, render a plain, honest note rather than a dead "#" link, matching the funnel's
// get-the-app screen behaviour.
function StoreBadge({ href, label }: { href: string; label: string }) {
  const style = { ...card, padding: '12px 18px', fontWeight: 800, fontSize: 14 } as const;
  if (!href) {
    return (
      <span style={{ ...style, color: 'var(--slate)' }} aria-disabled="true">
        {label} (coming soon)
      </span>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" style={style}>
      {label}
    </a>
  );
}

// Terminal, non-success states: used / expired / invalid / network error.
export function ClaimNotice({
  title,
  body,
  action,
  kind,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
  kind: ClaimKind;
}) {
  return (
    <ClaimShell badge={claimBadge(kind)}>
      <IconBadge>
        <svg width={28} height={28} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx={12} cy={12} r={9} stroke="var(--teal-deep)" strokeWidth={1.8} />
          <path d="M12 7.5V13" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" />
          <circle cx={12} cy={16.2} r={1.1} fill="var(--teal-deep)" />
        </svg>
      </IconBadge>
      <h1 tabIndex={-1} className="pretty" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.16, margin: 0 }}>
        {title}
      </h1>
      <p className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', margin: '12px 0 0', lineHeight: 1.55 }}>
        {body}
      </p>
      {action && <div style={{ marginTop: 26 }}>{action}</div>}
    </ClaimShell>
  );
}

export const adminSignInUrl = `${ADMIN_WEB}/login`;
